import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { CompanyProfileData, CompanyProfileTab } from '../../types/components';
import { useCompany } from '../../context/CompanyContext';
import { useAuth } from '../../hooks/useAuth';
import useDebounce from '../../hooks/useDebounce'; // Import useDebounce
import FileUploader from '../../components/ui/FileUploader';
import { Attachments } from '../../types/api';
import Toast from '../../components/ui/Toast';


const CompanyProfile = () => {
  const { companyDetails, loadingState, errorState, updateCompanyDetails, fetchCompanyDetails, updateCompanyAttachments } = useCompany();
  const { user } = useAuth();

  const [profileData, setProfileData] = useState<CompanyProfileData>({
    companyName: "",
    companyId: "",
    gstNumber: "",
    panNumber: "",
    msmeNumber: "",
    registrationType: 'gstin',
    registrationNumber: "",
    businessType: "",
    industry: "",
    companySize: "",
    foundedYear: "",
    website: "",
    description: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    contactPerson: "",
    contactEmail: "",
    contactPhone: "",
    hrPerson: "",
    hrEmail: "",
    hrPhone: "",
    linkedinUrl: "",
    twitterUrl: "",
    facebookUrl: "",
    instagramUrl: "",
    attachments: [],
    isVerified: false,
    verificationStatus: "pending",
  });
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);

  const [localWebsite, setLocalWebsite] = React.useState<string>('');
  const [websiteValidationMessage, setWebsiteValidationMessage] = React.useState<string | undefined>(undefined);
  const [websiteSuccessMessage, setWebsiteSuccessMessage] = React.useState<string | undefined>(undefined);
  const debouncedWebsite = useDebounce<string>(localWebsite, 1000);

  useEffect(() => {
    // Ensure we fetch latest company details when opening the profile
    if (!companyDetails && user && user.role === 'Company' && (user as any).companyId) {
      fetchCompanyDetails((user as any).companyId);
    }
  }, [companyDetails, user]);

  useEffect(() => {
    if (companyDetails) {
      let parsedOtherDetails: any = {};
      if (companyDetails.otherDetails) {
        try {
          parsedOtherDetails = JSON.parse(companyDetails.otherDetails);
        } catch (error) {
          console.error("Failed to parse otherDetails JSON:", error);
        }
      }

      setProfileData({
        companyName: companyDetails.companyName || "",
        companyId: companyDetails.companyId || "",
        gstNumber: companyDetails.gstin || "",
        industry: companyDetails.industry || "",
        companySize: companyDetails.empStrength || "",
        website: companyDetails.webpageUrl || "",
        description: companyDetails.description || "",
        address: companyDetails.address || "",
        isVerified: companyDetails.verificationStatus === "Verified",
        verificationStatus:
          companyDetails.verificationStatus.toLowerCase() || "pending",
        attachments: companyDetails.attachments || [],
        panNumber: parsedOtherDetails.panNumber || "",
        msmeNumber: parsedOtherDetails.msmeNumber || "",
        registrationType: parsedOtherDetails.registrationType || 'gstin',
        registrationNumber: parsedOtherDetails.registrationNumber || "",
        businessType: parsedOtherDetails.businessType || "",
        foundedYear: parsedOtherDetails.foundedYear || "",
        city: parsedOtherDetails.city || "",
        state: parsedOtherDetails.state || "",
        pincode: parsedOtherDetails.pincode || "",
        country: parsedOtherDetails.country || "",
        contactPerson: parsedOtherDetails.contactPerson || "",
        contactEmail: parsedOtherDetails.contactEmail || "",
        contactPhone: parsedOtherDetails.contactPhone || "",
        hrPerson: parsedOtherDetails.hrEmail || "",
        hrEmail: parsedOtherDetails.hrEmail || "",
        hrPhone: parsedOtherDetails.hrPhone || "",
        linkedinUrl: parsedOtherDetails.linkedinUrl || "",
        twitterUrl: parsedOtherDetails.twitterUrl || "",
        facebookUrl: parsedOtherDetails.facebookUrl || "",
        instagramUrl: parsedOtherDetails.instagramUrl || "",
      });
      setLocalWebsite(companyDetails.webpageUrl || '');
    }
  }, [companyDetails]);

  // Website validation logic
  React.useEffect(() => {
    if (debouncedWebsite) {
      validateWebsiteUrl(debouncedWebsite);
    } else {
      setWebsiteValidationMessage(undefined);
      setWebsiteSuccessMessage(undefined);
    }
  }, [debouncedWebsite]);

  React.useEffect(() => {
    setWebsiteValidationMessage(undefined);
    setWebsiteSuccessMessage(undefined);
  }, [localWebsite]);

  const validateWebsiteUrl = async (url: string): Promise<{ error: boolean; msg: string }> => {
    if (!url) {
      setWebsiteValidationMessage(undefined);
      setWebsiteSuccessMessage(undefined);
      return { error: false, msg: "" };
    }

    let formattedUrl = url;
    if (!/^https?:\/\//i.test(url)) {
      formattedUrl = `https://${url}`;
    }
    setLocalWebsite(formattedUrl)
    try {
      new URL(formattedUrl);
      setWebsiteValidationMessage(undefined);

      await fetch(formattedUrl, { method: 'HEAD', mode: 'no-cors' });
      setWebsiteValidationMessage(undefined);
      setWebsiteSuccessMessage('Website link is valid');

      return { error: false, msg: 'Website link is valid' };
    } catch (error) {
      setWebsiteValidationMessage('Please enter a valid and reachable website link');
      setWebsiteSuccessMessage(undefined);
      return { error: true, msg: 'Please enter a valid website link' };
    }
  };

  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("basic");
  
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    if (type === "file") {
      const fileInput = e.target as HTMLInputElement;
      setProfileData((prev) => ({
        ...prev,
        [name]: fileInput.files ? fileInput.files[0] : null,
      }));
    } else {
      setProfileData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  useEffect(() => {
    setShowSuccessMessage(false);
  }, [activeTab]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate website before submitting
    if (localWebsite && websiteValidationMessage) {
      setToast({ message: websiteValidationMessage, type: 'error' });
      return;
    }
    if (localWebsite && !websiteSuccessMessage) {
      const { error, msg } = await validateWebsiteUrl(localWebsite);
      if (error) {
        setToast({ message: msg, type: 'error' });
        return;
      }
    }

    try {
      await updateCompanyDetails({ ...profileData, website: localWebsite });
      setShowSuccessMessage(true);
    } catch (err: any) {
      console.error("Failed to update company profile:", err);
      setToast({ message: err.data?.message || (typeof err === 'string' ? err : "Something went wrong"), type: 'error' });
    }
  };

  const handleAttachmentUpload = (newAttachment: Attachments | null) => {
    if (!newAttachment || !newAttachment.typeId) {
      console.error("Uploaded attachment is missing or missing typeId:", newAttachment);
      setToast({ message: "Uploaded file is missing or missing type information.", type: "error" });
      return;
    }

    // Update local profileData state
    setProfileData((prevProfileData) => {
      const existingAttachments = prevProfileData.attachments || [];
      const updatedAttachments = existingAttachments
        .filter((att) => att.typeId !== newAttachment.typeId) // Remove old attachment of the same type
        .concat(newAttachment); // Add the new attachment

      return {
        ...prevProfileData,
        attachments: updatedAttachments,
      };
    });

    // Update companyDetails in context
    updateCompanyAttachments(newAttachment);
  };

  const tabs: CompanyProfileTab[] = [
    { id: "basic", name: "Basic Information", icon: "" },
    { id: "legal", name: "Company Details", icon: "" },
    { id: "contact", name: "Contact Information", icon: "" },
    { id: "social", name: "Social Media", icon: "" },
    { id: "documents", name: "Company Logo", icon: "" },
  ];

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <Input
            label="Company Name"
            name="companyName"
            value={profileData.companyName}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Select
            label="Industry"
            options={[
              { value: '', label: 'Select Industry' },
              { value: 'technology', label: 'Technology' },
              { value: 'finance', label: 'Finance & Banking' },
              { value: 'healthcare', label: 'Healthcare' },
              { value: 'education', label: 'Education' },
              { value: 'manufacturing', label: 'Manufacturing' },
              { value: 'retail', label: 'Retail & E-commerce' },
              { value: 'consulting', label: 'Consulting' },
              { value: 'other', label: 'Other' }
            ]}
            value={profileData.industry || ''}
            onChange={(value) => setProfileData(prev => ({ ...prev, industry: value }))}
            required
            showSearchButton={false}
          />
        </div>
        <div>
          <Select
            label="Company Size"
            options={[
              { value: '', label: 'Select Company Size' },
              { value: '1-10', label: '1-10 employees' },
              { value: '11-50', label: '11-50 employees' },
              { value: '51-200', label: '51-200 employees' },
              { value: '201-500', label: '201-500 employees' },
              { value: '500+', label: '500+ employees' }
            ]}
            value={profileData.companySize || ''}
            onChange={(value) => setProfileData(prev => ({ ...prev, companySize: value }))}
            required
            showSearchButton={false}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Founded Year"
          name="foundedYear"
          type="number"
          value={profileData.foundedYear}
          onChange={handleInputChange}
          required
        />
        <Input
          label="Website"
          name="website"
          type="url"
          value={localWebsite}
          onChange={(e) => {
            setLocalWebsite(e.target.value);
            handleInputChange(e); // Keep existing profileData update
          }}
          error={websiteValidationMessage}
          success={websiteSuccessMessage}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Company Description
        </label>
        <textarea
          name="description"
          rows={4}
          value={profileData.description}
          onChange={handleInputChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
          placeholder="Tell us about your company..."
          required
        />
      </div>
    </div>
  );

  const renderLegalDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="GST Number"
          name="gstNumber"
          value={profileData.gstNumber}
          onChange={handleInputChange}
        />
        <Input
          label="PAN Number"
          name="panNumber"
          value={profileData.panNumber}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="CIN (Company Incorporation Number)"
          name="registrationNumber"
          value={profileData.registrationNumber}
          onChange={handleInputChange}
        />
        <div>
          <Select
            label="Business Type"
            options={[
              { value: '', label: 'Select Business Type' },
              { value: 'private-limited', label: 'Private Limited' },
              { value: 'public-limited', label: 'Public Limited' },
              { value: 'llp', label: 'Limited Liability Partnership' },
              { value: 'partnership', label: 'Partnership' },
              { value: 'sole-proprietorship', label: 'Sole Proprietorship' },
              { value: 'ngo', label: 'NGO/Trust' }
            ]}
            value={profileData.businessType || ''}
            onChange={(value) => setProfileData(prev => ({ ...prev, businessType: value }))}
            showSearchButton={false}
          />
        </div>
      </div>

      {/* Document Upload Section */}
      <div className="mt-8">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Legal Documents (Optional)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FileUploader
            label="GST Registration Certificate"
            name="GST Certificate"
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={10 * 1024 * 1024} // 10MB
            // onFileSelect={(file) => updateFormData({ gstCertificate: file })}
            attachment={companyDetails?.attachments?.find(a=>a.typeId=='CompanyDocumentGST')}
            referenceId={companyDetails?.companyId || ''}
            onFileUploaded={handleAttachmentUpload}
            description="Upload your GST registration certificate (one file)"
            uploadType='CompanyDocumentGST'
          />

          <FileUploader
            label="PAN Card Copy"
            name="Pan Card"
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={10 * 1024 * 1024} // 10MB
            attachment={companyDetails?.attachments?.find(a=>a.typeId=='CompanyDocumentPAN')}
            referenceId={companyDetails?.companyId || ''}
            onFileUploaded={handleAttachmentUpload}
            description="Upload a clear copy of your PAN card"
            uploadType='CompanyDocumentPAN'
          />
          <FileUploader
            label="MSME Registration Certificate"
            name="MSME Certificate"
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={10 * 1024 * 1024} // 10MB
            attachment={companyDetails?.attachments?.find(a=>a.typeId=='CompanyDocumentMSME')}
            referenceId={companyDetails?.companyId || ''}
            onFileUploaded={handleAttachmentUpload}
            description="Upload your MSME registration certificate"
            uploadType='CompanyDocumentMSME'
          />
          <FileUploader
            label="CIN Certificate"
            name="CIN Certificate"
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={10 * 1024 * 1024} // 10MB
            attachment={companyDetails?.attachments?.find(a=>a.typeId=='CompanyDocumentCIN')}
            referenceId={companyDetails?.companyId || ''}
            onFileUploaded={handleAttachmentUpload}
            description="Upload your Certificate of Incorporation"
            uploadType='CompanyDocumentCIN'
          />

          {/* <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 48 48">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" />
            </svg>
            <div className="mb-4">
              <label htmlFor="cin-certificate" className="cursor-pointer">
                <span className="block text-sm font-medium text-gray-900 mb-1">
                  CIN Certificate
                </span>
                <span className="block text-sm text-gray-500">
                  PDF up to 10MB
                </span>
              </label>
              <input
                id="cin-certificate"
                type="file"
                accept=".pdf"
                className="sr-only"
                onChange={(e) => handleFileChange('cinCertificate', e)}
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => document.getElementById('cin-certificate')?.click()}>
              Upload CIN Certificate
            </Button>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 48 48">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" />
            </svg>
            <div className="mb-4">
              <label htmlFor="certificate-incorporation" className="cursor-pointer">
                <span className="block text-sm font-medium text-gray-900 mb-1">
                  Certificate of Incorporation
                </span>
                <span className="block text-sm text-gray-500">
                  PDF up to 10MB
                </span>
              </label>
              <input
                id="certificate-incorporation"
                type="file"
                accept=".pdf"
                className="sr-only"
                onChange={(e) => handleFileChange('certificateIncorporation', e)}
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => document.getElementById('certificate-incorporation')?.click()}>
              Upload Certificate
            </Button>
          </div> */}
        </div>
      </div>
    </div>
  );

  const renderContactInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Contact Person"
          name="contactPerson"
          value={profileData.contactPerson}
          onChange={handleInputChange}
          required
        />
        <Input
          label="Contact Email"
          name="contactEmail"
          type="email"
          value={profileData.contactEmail}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Contact Phone"
          name="contactPhone"
          value={profileData.contactPhone}
          onChange={handleInputChange}
          required
        />
        <Input
          label="HR Email"
          name="hrEmail"
          type="email"
          value={profileData.hrEmail}
          onChange={handleInputChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="HR Phone"
          name="hrPhone"
          value={profileData.hrPhone}
          onChange={handleInputChange}
        />
        <Input
          label="Address"
          name="address"
          value={profileData.address}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Input
          label="City"
          name="city"
          value={profileData.city}
          onChange={handleInputChange}
          required
        />
        <Input
          label="State"
          name="state"
          value={profileData.state}
          onChange={handleInputChange}
          required
        />
        <Input
          label="PIN Code"
          name="pincode"
          value={profileData.pincode}
          onChange={handleInputChange}
          required
        />
      </div>
    </div>
  );

  const renderSocialMedia = () => (
    <div className="space-y-6">
      <Input
        label="LinkedIn URL"
        name="linkedinUrl"
        type="url"
        value={profileData.linkedinUrl}
        onChange={handleInputChange}
        placeholder="https://linkedin.com/company/yourcompany"
      />

      <Input
        label="Twitter URL"
        name="twitterUrl"
        type="url"
        value={profileData.twitterUrl as any}
        onChange={handleInputChange}
        placeholder="https://twitter.com/yourcompany"
      />

      <Input
        label="Facebook URL"
        name="facebookUrl"
        type="url"
        value={profileData.facebookUrl}
        onChange={handleInputChange}
        placeholder="https://facebook.com/yourcompany"
      />

      <Input
        label="Instagram URL"
        name="instagramUrl"
        type="url"
        value={profileData.instagramUrl}
        onChange={handleInputChange}
        placeholder="https://instagram.com/yourcompany"
      />
    </div>
  );

  const renderDocuments = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:space-x-6 space-y-6 md:space-y-0">
        <FileUploader
          label="Update Company Logo"
          name="Company Logo"
          accept=".jpg,.png,.jpeg"
          attachment={companyDetails?.attachments?.find(a=>a.typeId=='CompanyLogo')}
          referenceId= {companyDetails?.companyId || ''}
          onFileUploaded={handleAttachmentUpload}
          uploadType="CompanyLogo"
        />
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          Verification Status
        </h4>
        <div className="flex items-center space-x-2">
          <div
            className={`w-3 h-3 rounded-full ${
              profileData.verificationStatus === "verified"
                ? "bg-green-400"
                : profileData.verificationStatus === "pending"
                ? "bg-yellow-400"
                : "bg-red-400"
            }`}
          ></div>
          <span className="text-sm text-gray-600 capitalize">
            {profileData.verificationStatus}
          </span>
        </div>
        {profileData.verificationStatus === "pending" && (
          <p className="text-xs text-gray-500 mt-1">
            Your documents are being reviewed. You will receive approval within
            24-48 hours.
          </p>
        )}
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "basic":
        return renderBasicInfo();
      case "legal":
        return renderLegalDetails();
      case "contact":
        return renderContactInfo();
      case "social":
        return renderSocialMedia();
      case "documents":
        return renderDocuments();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <div className="max-w-4xl mx-auto md:p-6">
      {(toast) && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={2500}
          onClose={() => setToast(null)}
          show
        />
      )}
      {errorState?.companyDetails && (
        <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-700">{errorState.companyDetails}</div>
      )}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Company Profile
        </h1>
        <p className="text-gray-600">
          Manage your company information and settings
        </p>
      </div>

      {/* Clean Text-Only Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8 overflow-hidden">
        <nav className="flex overflow-x-auto scrollbar-hide">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center px-6 py-4 font-medium text-sm transition-all duration-200 relative ${
                activeTab === tab.id
                  ? "bg-primary-lightGreen/10 text-primary-lightGreen border-b-2 border-primary-lightGreen"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              } ${index !== tabs.length - 1 ? 'border-r border-gray-100' : ''}`}
            >
              <span className="whitespace-nowrap">{tab.name}</span>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen"></div>
              )}
            </button>
          ))}
        </nav>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          {loadingState?.companyDetails && (
            <div className="mb-4 text-sm text-gray-500">Loading company details…</div>
          )}
          {renderTabContent()}
        </div>

        {showSuccessMessage && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Profile Updated Successfully!
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>
                    Your company profile has been updated.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button loading={loadingState?.updateCompanyDetails} type="submit" className="px-8" disabled={loadingState?.updateCompanyDetails}>
            Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompanyProfile;
